import * as THREE from 'three';
import { RenderStage } from './SceneConfig';

export interface RenderStats {
  fps: number;
  frameTime: number;
  drawCalls: number;
  triangles: number;
  geometries: number;
  textures: number;
  memoryUsage: number;
}

export class RenderLoop {
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.Camera;
  
  // Render stages
  private preRenderStages: RenderStage[] = [];
  private postRenderStages: RenderStage[] = [];
  
  // Performance monitoring
  private lastFrameTime: number = performance.now();
  private frameCount: number = 0;
  private fpsHistory: number[] = [];
  private readonly FPS_HISTORY_SIZE = 60;
  
  // Animation loop
  private animationId: number | null = null;
  private isRunning: boolean = false;
  
  // Callbacks
  private onFrame: ((deltaTime: number) => void) | undefined;
  private onStatsUpdate: ((stats: RenderStats) => void) | undefined;
  
  constructor(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera
  ) {
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;
  }
  
  setOnFrame(callback: (deltaTime: number) => void): void {
    this.onFrame = callback;
  }
  
  setOnStatsUpdate(callback: (stats: RenderStats) => void): void {
    this.onStatsUpdate = callback;
  }
  
  addPreRenderStage(stage: RenderStage): void {
    this.preRenderStages.push(stage);
  }
  
  addPostRenderStage(stage: RenderStage): void {
    this.postRenderStages.push(stage);
  }
  
  removeStage(id: string): boolean {
    const preIndex = this.preRenderStages.findIndex(stage => stage.id === id);
    if (preIndex !== -1) {
      this.preRenderStages.splice(preIndex, 1);
      return true;
    }
    
    const postIndex = this.postRenderStages.findIndex(stage => stage.id === id);
    if (postIndex !== -1) {
      this.postRenderStages.splice(postIndex, 1);
      return true;
    }
    
    return false;
  }
  
  start(): void {
    if (this.isRunning) return;
    
    this.isRunning = true;
    this.lastFrameTime = performance.now();
    this.animate();
  }
  
  stop(): void {
    this.isRunning = false;
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }
  
  private animate = (): void => {
    if (!this.isRunning) return;
    
    this.animationId = requestAnimationFrame(this.animate);
    
    const currentTime = performance.now();
    const deltaTime = (currentTime - this.lastFrameTime) / 1000; // Convert to seconds
    this.lastFrameTime = currentTime;
    
    // Execute pre-render stages
    for (const stage of this.preRenderStages) {
      if (stage.enabled) {
        stage.render(this.renderer, this.scene, this.camera);
      }
    }
    
    // Main render
    this.renderer.render(this.scene, this.camera);
    
    // Execute post-render stages
    for (const stage of this.postRenderStages) {
      if (stage.enabled) {
        stage.render(this.renderer, this.scene, this.camera);
      }
    }
    
    // Update performance stats
    this.updateStats(deltaTime);
    
    // Call frame callback
    if (this.onFrame) {
      this.onFrame(deltaTime);
    }
    
    this.frameCount++;
  };
  
  private updateStats(deltaTime: number): void {
    // Calculate FPS
    const fps = 1 / deltaTime;
    this.fpsHistory.push(fps);
    
    if (this.fpsHistory.length > this.FPS_HISTORY_SIZE) {
      this.fpsHistory.shift();
    }
    
    // Update stats every 60 frames (~1 second at 60fps)
    if (this.frameCount % 60 === 0 && this.onStatsUpdate) {
      const averageFPS = this.fpsHistory.reduce((sum, f) => sum + f, 0) / this.fpsHistory.length;
      
      const stats: RenderStats = {
        fps: averageFPS,
        frameTime: deltaTime * 1000, // Convert to ms
        drawCalls: this.renderer.info.render.calls,
        triangles: this.renderer.info.render.triangles,
        geometries: this.renderer.info.memory.geometries,
        textures: this.renderer.info.memory.textures,
        memoryUsage: (performance as any).memory?.usedJSHeapSize || 0,
      };
      
      this.onStatsUpdate(stats);
    }
  }
  
  setQuality(level: 'low' | 'medium' | 'high' | 'ultra'): void {
    const pixelRatio = window.devicePixelRatio;
    
    switch (level) {
      case 'low':
        this.renderer.setPixelRatio(Math.min(pixelRatio, 1));
        this.renderer.shadowMap.enabled = false;
        break;
        
      case 'medium':
        this.renderer.setPixelRatio(Math.min(pixelRatio, 1.5));
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFShadowMap;
        break;
        
      case 'high':
        this.renderer.setPixelRatio(Math.min(pixelRatio, 2));
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        break;
        
      case 'ultra':
        this.renderer.setPixelRatio(pixelRatio);
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.VSMShadowMap;
        break;
    }
  }
  
  getCurrentStats(): RenderStats {
    const averageFPS = this.fpsHistory.length > 0 
      ? this.fpsHistory.reduce((sum, f) => sum + f, 0) / this.fpsHistory.length 
      : 0;
    
    return {
      fps: averageFPS,
      frameTime: this.fpsHistory.length > 0 ? 1000 / averageFPS : 0,
      drawCalls: this.renderer.info.render.calls,
      triangles: this.renderer.info.render.triangles,
      geometries: this.renderer.info.memory.geometries,
      textures: this.renderer.info.memory.textures,
      memoryUsage: (performance as any).memory?.usedJSHeapSize || 0,
    };
  }
  
  dispose(): void {
    this.stop();
    this.preRenderStages.length = 0;
    this.postRenderStages.length = 0;
    this.onFrame = undefined as ((deltaTime: number) => void) | undefined;
    this.onStatsUpdate = undefined as ((stats: RenderStats) => void) | undefined;
  }
}